import { docSlugFromPageId } from "@repo/shared";
import type { DocSearchResult } from "@repo/shared/types";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ChevronRight, Loader2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { MarkdownRendererClient } from "@/components/markdown/markdown-renderer-client";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { SearchInput } from "@/components/ui/search-input";
import { Separator } from "@/components/ui/separator";
import {
  type BreadcrumbItem,
  type DocPage,
  getBreadcrumbs,
  getDocPage,
  searchDocs,
} from "@/lib/api/docs";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./documentation-panel.i18n";

export function DocumentationPanel({ isActive }: { isActive?: boolean }) {
  const queryClient = useQueryClient();
  const t = useT(T);

  const [page, setPage] = useState<DocPage | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pageError, setPageError] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<DocSearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  // The panel sits outside the router, so browser Back leaves the app page
  // instead of the doc. Keep our own stack of previously visited slugs.
  const [history, setHistory] = useState<string[]>([]);
  // Slug of the page on screen. Set only once a load succeeds.
  const shownSlugRef = useRef<string | null>(null);
  // Only the latest request may update the panel; a slower earlier response
  // would otherwise overwrite the page the user moved on to.
  const loadIdRef = useRef(0);
  const headingRef = useRef<HTMLHeadingElement>(null);

  // History changes only when a load succeeds, so a failed or superseded
  // navigation never leaves a Back entry behind.
  const openDoc = useCallback(
    async (slug: string, { isBack = false, focusHeading = false } = {}) => {
      const loadId = ++loadIdRef.current;
      setIsLoading(true);
      setPageError(false);
      try {
        const [docPage, crumbs] = await Promise.all([
          getDocPage(slug),
          getBreadcrumbs(slug, queryClient),
        ]);
        if (loadId !== loadIdRef.current) return;
        const previous = shownSlugRef.current;
        if (isBack) {
          setHistory((prev) => prev.slice(0, -1));
        } else if (previous && previous !== slug) {
          setHistory((prev) => [...prev, previous]);
        }
        shownSlugRef.current = slug;
        setPage(docPage);
        setBreadcrumbs(crumbs);
        // The back button unmounts with the last entry; keep focus in the panel.
        if (focusHeading) headingRef.current?.focus();
      } catch {
        if (loadId !== loadIdRef.current) return;
        // The translated "page not found" heading says it; the raw error is English.
        setPageError(true);
        setPage(null);
      } finally {
        if (loadId === loadIdRef.current) setIsLoading(false);
      }
    },
    [queryClient],
  );

  function goBack() {
    const previous = history.at(-1);
    if (!previous) return;
    openDoc(previous, { isBack: true, focusHeading: history.length === 1 });
  }

  // Keep a stable ref to openDoc so event listeners always call the latest version
  const openDocRef = useRef(openDoc);
  useEffect(() => {
    openDocRef.current = openDoc;
  }, [openDoc]);

  // Load index page on first activation
  useEffect(() => {
    if (!isActive) return;
    if (!page) openDocRef.current("index");

    const handleNavigateToDoc = (event: CustomEvent) => {
      openDocRef.current(docSlugFromPageId(event.detail.slug || event.detail.pageId));
    };
    window.addEventListener("navigate-to-doc", handleNavigateToDoc as EventListener);
    return () =>
      window.removeEventListener("navigate-to-doc", handleNavigateToDoc as EventListener);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  // Debounced search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    const id = setTimeout(async () => {
      try {
        const result = await searchDocs(searchQuery);
        setSearchResults(result.results);
      } catch {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(id);
  }, [searchQuery]);

  function goHome() {
    openDoc("index");
  }

  function handleDocLink(slug: string) {
    openDoc(slug);
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-2 px-6 h-16">
        {history.length > 0 && (
          <Button variant="ghost" size="icon" tooltip={t.back} onClick={goBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
        )}
        <h2 ref={headingRef} tabIndex={-1} className="text-xl font-semibold outline-none">
          {t.documentation}
        </h2>
      </div>

      <Separator />

      {/* Breadcrumbs */}
      {page && (
        <div className="flex items-center gap-1 text-xs text-muted-foreground flex-wrap px-6 pt-3 pb-2">
          <button
            type="button"
            onClick={goHome}
            className={`transition-colors ${
              breadcrumbs.length === 0 && page.slug === "index"
                ? "font-medium text-foreground"
                : "hover:text-foreground"
            }`}
          >
            Home
          </button>

          {breadcrumbs.map((item) => (
            <div key={item.slug} className="flex items-center gap-1">
              <ChevronRight className="h-3 w-3 flex-shrink-0" />
              <button
                type="button"
                onClick={() => openDoc(item.slug)}
                className="hover:text-foreground transition-colors"
              >
                {item.title}
              </button>
            </div>
          ))}

          {page.slug !== "index" && (
            <>
              <ChevronRight className="h-3 w-3 flex-shrink-0" />
              <span className="font-medium text-foreground">{page.title}</span>
            </>
          )}
        </div>
      )}

      {/* Search */}
      <div className="px-6 pt-2 pb-2">
        <SearchInput
          placeholder={t.searchPlaceholder}
          value={searchQuery}
          onValueChange={setSearchQuery}
        />
      </div>

      {/* Search Results */}
      {searchQuery && (
        <div className="border-b">
          <ScrollArea className="h-64">
            <div className="p-2 space-y-1 mx-4">
              {searchLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : searchResults.length === 0 ? (
                <p className="p-4 text-sm text-muted-foreground text-center">
                  {t.noResults} &quot;{searchQuery}&quot;
                </p>
              ) : (
                searchResults.map((result) => (
                  <button
                    type="button"
                    key={result.pageId}
                    className="w-full text-left rounded-md hover:bg-accent p-3 cursor-pointer transition-colors"
                    onClick={() => {
                      openDoc(docSlugFromPageId(result.pageId));
                      setSearchQuery("");
                    }}
                  >
                    <div className="font-medium text-sm mb-1">{result.title}</div>
                    {result.snippet && (
                      <div className="text-xs text-muted-foreground line-clamp-2">
                        {result.snippet}
                      </div>
                    )}
                  </button>
                ))
              )}
            </div>
          </ScrollArea>
        </div>
      )}

      {/* Content */}
      <ScrollArea className="flex-1">
        {isLoading ? (
          <div className="px-6 py-8 flex flex-col items-center justify-center text-center space-y-4">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">{t.loadingDocs}</p>
          </div>
        ) : pageError ? (
          <div className="px-6 py-8">
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-destructive mb-1">{t.pageNotFound}</h3>
              <button
                type="button"
                onClick={goHome}
                className="mt-3 text-xs text-primary hover:underline"
              >
                {t.returnToHome}
              </button>
            </div>
          </div>
        ) : page ? (
          <div className="px-6 pt-2 pb-4">
            {page.markdownContent ? (
              <MarkdownRendererClient content={page.markdownContent} onPageClick={handleDocLink} />
            ) : (
              <div className="text-sm text-muted-foreground">{t.noContent}</div>
            )}
          </div>
        ) : null}
      </ScrollArea>
    </div>
  );
}
